import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Contact } from '../components/Contact';
import { BUSINESS_FACTS, PRICING_TABLE, EXTRAS_TABLE, FAQ_CATEGORIES, ALL_FAQS, CITY_PAGE_AREAS } from '../data/knowledgeBase';
import { businessEntity, websiteEntity, injectJsonLd, setHeadMeta, canonicalUrl, BUSINESS_ID, WEBSITE_ID, SITE_URL } from '../utils/seo';
const PATH = '/faq';
const TITLE = "Mobile Detailing FAQ & Pricing | Mike's Pro Detail Ventura County";
const DESCRIPTION = "Answers to common questions about mobile car detailing from Mike's Pro Detail — pricing, what's included, service areas, scheduling, and payment. Call (818) 381-9905.";
/**
 * /faq — the customer information page.
 *
 * A single, plain-text, crawlable page carrying the official answer to every
 * common customer question, plus the pricing table and business facts. Built
 * entirely from the shared knowledge base so nothing here can contradict the
 * rest of the site. Designed to match the existing site exactly.
 */
export function FaqPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setHeadMeta({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      ogType: 'article'
    });
    const url = canonicalUrl(PATH);
    return injectJsonLd([businessEntity, websiteEntity, {
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      url,
      name: 'Mobile Car Detailing FAQ',
      description: DESCRIPTION,
      isPartOf: {
        '@id': WEBSITE_ID
      },
      about: {
        '@id': BUSINESS_ID
      },
      mainEntity: ALL_FAQS.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer
        }
      }))
    }, {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'FAQ',
        item: url
      }]
    }]);
  }, []);
  return <main className="min-h-screen w-full bg-black text-white selection:bg-red-600 selection:text-white" data-id="element-827" data-mp="1juodbordlyg4">
      <Navigation data-id="element-828" data-mp="1otsk68qtvk3a" />

      {/* Header */}
      <header className="w-full bg-black pt-32 pb-16 px-4 border-b border-zinc-900" data-id="element-829" data-mp="1oshhydd16kea">
        <div className="max-w-4xl mx-auto" data-id="element-830" data-mp="1a4mlqmztdxwg">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-831" data-mp="1a2s1lawda9tf">
            <ol className="flex items-center gap-2 text-sm text-gray-500" data-id="element-832" data-mp="1ewmu93ty9z4n">
              <li data-id="element-833" data-mp="114gbd9zexe1i">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-834" data-mp="1159d1wjildcs">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" className="text-zinc-600" data-id="element-835" data-mp="1616uk66ikpr5">
                /
              </li>
              <li className="text-gray-300" aria-current="page" data-id="element-836" data-mp="r83l6m32tibi">
                FAQ
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl sm:text-4xl md:text-6xl font-black uppercase mb-4" data-id="element-837" data-mp="cis5jw7vbhba">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-838" data-mp="8hq7vjtlndbf">
              Mobile Detailing FAQ &amp; Customer Information
            </span>
          </h1>
          <div className="h-1 w-24 bg-red-600 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-839" data-mp="hhv9fdc43hho" />
          <p className="text-gray-400 text-lg mt-6 max-w-3xl leading-relaxed" data-id="element-840" data-mp="vho23e7787sa">
            Everything you need to know about mobile car detailing with Mike's
            Pro Detail — pricing, what each package includes, the areas we
            serve, how to book, and what we need from you. If your question
            isn't answered here, call or text{' '}
            <a href="tel:8183819905" className="text-red-500 hover:underline" data-id="element-841" data-mp="10b8o07vipbb4">
              (818) 381-9905
            </a>
            .
          </p>
        </div>
      </header>

      {/* Business at a glance */}
      <section className="w-full bg-black py-12 px-4" aria-labelledby="business-facts" data-id="element-842" data-mp="33pssn8u8uu6u">
        <div className="max-w-4xl mx-auto" data-id="element-843" data-mp="33nyh2l4v4eff">
          <h2 id="business-facts" className="text-2xl md:text-3xl font-black uppercase text-white mb-6" data-id="element-844" data-mp="38nuchvzhuxjd">
            <span className="text-red-600" data-id="element-845" data-mp="3dgnjyrmckjz5">Business</span> at a Glance
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-846" data-mp="3ieyruwt30keq">
            {BUSINESS_FACTS.map(fact => <div key={fact.label} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5" data-id="element-847" data-mp="2krgmlvymq0fh">
                <dt className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-848" data-mp="2pp8b0g02f4ex">
                  {fact.label}
                </dt>
                <dd className="text-gray-200 leading-relaxed" data-id="element-849" data-mp="2ur7f5s3mq5cx">{fact.value}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Pricing */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby="faq-pricing" data-id="element-850" data-mp="9xu7yj97w373">
        <div className="max-w-4xl mx-auto" data-id="element-851" data-mp="ewnj45117i43">
          <h2 id="faq-pricing" className="text-2xl md:text-3xl font-black uppercase text-white mb-2" data-id="element-852" data-mp="1zg14h1paux10">
            <span className="text-red-600" data-id="element-853" data-mp="1ugxx3alukepv">Packages</span> &amp; Pricing
          </h2>
          <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-854" data-mp="1ujaftpzfit3z">
            Every package is priced in two tiers: SM/MID for sedans and smaller
            vehicles, and SUV/CROSS for SUVs and crossovers. Prices range from
            $90 to $350.
          </p>

          <div className="space-y-4" data-id="element-855" data-mp="1fstcu275bv4z">
            {PRICING_TABLE.map(pkg => <div key={pkg.name} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-6" data-id="element-856" data-mp="1bpx2szvo97cn">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3" data-id="element-857" data-mp="16qtun6keal0g">
                  <h3 className="text-xl font-black uppercase text-white" data-id="element-858" data-mp="11simr1dnukkv">
                    {pkg.name}
                  </h3>
                  <p className="text-gray-300 font-bold" data-id="element-859" data-mp="314bnn93kka2v">
                    SM / MID{' '}
                    <span className="text-red-500" data-id="element-860" data-mp="2w7bgeuuzh1a6">{pkg.smMid}</span>
                    <span className="text-zinc-700 mx-2" data-id="element-861" data-mp="uhe0jokhzlyf">·</span>
                    SUV / CROSS{' '}
                    <span className="text-red-500" data-id="element-862" data-mp="z7kn7ivg8we1">{pkg.suvCross}</span>
                  </p>
                </div>
                <p className="text-gray-400 mb-3 leading-relaxed" data-id="element-863" data-mp="kjgacvj2vf36">{pkg.note}</p>
                <p className="text-gray-300 leading-relaxed" data-id="element-864" data-mp="pi154ffk96k8">
                  <span className="font-bold text-white" data-id="element-865" data-mp="piav430lvdpx">Includes: </span>
                  {pkg.includes.join(', ')}.
                </p>
              </div>)}
          </div>

          <h3 className="text-lg font-black uppercase text-white mt-8 mb-4" data-id="element-866" data-mp="1i5nhod6rnlj8">
            Extra Services
          </h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-867" data-mp="138wd7pi8jjmr">
            {EXTRAS_TABLE.map(extra => <div key={extra.name} className="flex items-center justify-between gap-4 bg-zinc-900/40 border border-zinc-800 rounded-xl px-5 py-4" data-id="element-868" data-mp="186xx2cnhjmza">
                <dt className="text-gray-200 font-bold" data-id="element-869" data-mp="3syhy07b756xn">{extra.name}</dt>
                <dd className="text-red-500 font-black whitespace-nowrap" data-id="element-870" data-mp="3swncaqs3pasi">
                  {extra.price}
                </dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* FAQ categories */}
      {FAQ_CATEGORIES.map(category => <section key={category.id} id={category.id} className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby={`${category.id}-heading`} data-id="element-871" data-mp="2k2304brw73on">
          <div className="max-w-4xl mx-auto" data-id="element-872" data-mp="2fyd97fnqwa0g">
            <h2 id={`${category.id}-heading`} className="text-2xl md:text-3xl font-black uppercase text-white mb-3" data-id="element-873" data-mp="1sbopqpj389hq">
              {category.title}
            </h2>
            <p className="text-gray-400 mb-6 leading-relaxed max-w-3xl" data-id="element-874" data-mp="212p1hgj7x4rt">
              {category.intro}
            </p>
            <dl className="space-y-4" data-id="element-875" data-mp="1wbyu5xr8eruh">
              {category.faqs.map(faq => <div key={faq.question} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-6" data-id="element-876" data-mp="18mmov8z0z3xg">
                  <dt className="text-white font-bold text-lg" data-id="element-877" data-mp="14l183cjde5hz">
                    {faq.question}
                  </dt>
                  <dd className="mt-3 text-gray-300 text-base leading-relaxed" data-id="element-878" data-mp="1tglo7yhp73w1">
                    {faq.answer}
                  </dd>
                </div>)}
            </dl>
          </div>
        </section>)}

      {/* Where to learn more */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby="learn-more" data-id="element-879" data-mp="22f70xd0hnoow">
        <div className="max-w-4xl mx-auto" data-id="element-880" data-mp="27didhvi64dat">
          <h2 id="learn-more" className="text-2xl md:text-3xl font-black uppercase text-white mb-6" data-id="element-881" data-mp="2cfhgv5dwra7r">
            Learn More About Each <span className="text-red-600" data-id="element-882" data-mp="2hchm5e2xo8xt">Service</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-883" data-mp="2m3zexeusz37p">
            <FaqLink to="/services/mobile-detailing" label="Mobile Detailing" data-id="element-884" data-mp="2q63230qu3w8t" />
            <FaqLink to="/services/full-car-detail" label="Full Car Detail" data-id="element-885" data-mp="2q8739axcmh5e" />
            <FaqLink to="/services/interior-detailing" label="Interior Detailing" data-id="element-886" data-mp="2v6qr5gvi630e" />
            <FaqLink to="/services/exterior-detailing" label="Exterior Detailing" data-id="element-887" data-mp="305bqle2xkins" />
            <FaqLink to="/services/ceramic-coating" label="Ceramic Coating" data-id="element-888" data-mp="3a8h3uaypay22" />
            <FaqLink to="/services/headlight-restoration" label="Headlight Restoration" data-id="element-889" data-mp="35949741l8mox" />
            <FaqLink to="/services/engine-bay-detailing" label="Engine Bay Detailing" data-id="element-890" data-mp="30a1bk4nxtiqz" />
          </div>

          <h3 className="text-lg font-black uppercase text-white mb-4" data-id="element-891" data-mp="2w8fsu2oq2a8v">
            Service Areas
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-892" data-mp="3syf80fzdo70f">
            {CITY_PAGE_AREAS.map(city => <FaqLink key={city} to={`/ventura-county/${city.toLowerCase().replace(/\s+/g, '-')}`} label={city} data-id="element-893" data-mp="3nzvpkpjztdct" />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" data-id="element-894" data-mp="3j6j0yffbd6k2">
        <div className="max-w-4xl mx-auto text-center" data-id="element-895" data-mp="3f71v3kvz67ed">
          <h2 className="text-2xl md:text-3xl font-black uppercase text-white mb-3" data-id="element-896" data-mp="fk24ryimpfpy">
            Still Have a Question?
          </h2>
          <p className="text-gray-400 mb-6" data-id="element-897" data-mp="arskoz4739vl">
            Call or text Mike directly and get a straight answer.
          </p>
          <a href="tel:8183819905" className="inline-flex items-center gap-3 px-8 py-4 bg-red-600 text-white font-black uppercase tracking-wide rounded-lg hover:bg-red-500 transition-colors duration-150" aria-label="Call Mike's Pro Detail at (818) 381-9905" data-id="element-898" data-mp="lihq8o9tq5wo">
            <Phone className="w-5 h-5" aria-hidden="true" data-id="element-899" data-mp="phgvyb3dq6p6" />
            (818) 381-9905
          </a>
        </div>
      </section>

      <Contact data-id="element-900" data-mp="14dekb772a6qu" />
    </main>;
}
function FaqLink({
  to,
  label
}: {
  to: string;
  label: string;
}) {
  return <Link to={to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-colors duration-150" data-id="element-901" data-mp="zelg65dnzs3c">
      {label} &rarr;
    </Link>;
}